import { EnhancedDeal, PipelineStage } from '../types';

export interface ThreatAssessment {
  score: number;           // 0-100
  band: 'critical' | 'high' | 'moderate' | 'low';
  signals: ThreatSignal[];
}

interface ThreatSignal {
  label: string;
  value: number;       // 0-100 contribution before weighting
  weight: number;      // percentage of total score
  detail: string;
}

function clamp(v: number, min: number, max: number) {
  return Math.max(min, Math.min(max, v));
}

function getDaysFromDate(date: Date | string | undefined): number {
  if (!date) return 0;
  const d = new Date(date);
  if (isNaN(d.getTime()) || d.getTime() < new Date('2000-01-01').getTime()) return 0;
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  return days < 0 ? 0 : days;
}

/**
 * Compute a threat score for a deal: how urgently it needs sales attention.
 * Higher = more urgent. Purely client-side, no writes.
 */
export function computeThreatScore(
  deal: EnhancedDeal,
  stage: PipelineStage | undefined,
  totalStages: number,
  allDeals: EnhancedDeal[],
): ThreatAssessment {
  const signals: ThreatSignal[] = [];

  const daysInStage = getDaysFromDate(deal.currentStageEnteredAt || deal.createdAt);
  const rottingDays = stage?.rottingDays ?? 30;

  // 1. Stage stall ratio (30pts) — how far past the rotting threshold
  const stallRatio = clamp(daysInStage / rottingDays, 0, 3);
  const stallScore = Math.round((stallRatio / 3) * 100);
  signals.push({
    label: 'Stage stall',
    value: stallScore,
    weight: 30,
    detail: `${daysInStage}d in stage (limit: ${rottingDays}d)`,
  });

  // 2. Weighted value at risk (25pts) — higher weighted value = higher priority to save
  const weightedValue = deal.value * (deal.probability / 100);
  const maxWeighted = Math.max(
    ...allDeals.filter(d => d.outcome === 'active').map(d => d.value * (d.probability / 100)),
    1,
  );
  const valueScore = Math.round(clamp(weightedValue / maxWeighted, 0, 1) * 100);
  signals.push({
    label: 'Value at risk',
    value: valueScore,
    weight: 25,
    detail: `$${Math.round(weightedValue).toLocaleString()} weighted`,
  });

  // 3. Stage regression risk (15pts) — later-stage stalls hurt more
  const stageOrder = stage?.order ?? 1;
  const regressionScore = totalStages > 1
    ? Math.round((stageOrder / totalStages) * 100)
    : 50;
  signals.push({
    label: 'Stage depth',
    value: regressionScore,
    weight: 15,
    detail: `Stage ${stageOrder} of ${totalStages}`,
  });

  // 4. Velocity decay (15pts) — deals that moved fast then stopped are alarming
  let velocityScore = 50;
  if (deal.stageHistory && deal.stageHistory.length > 0) {
    const avgTransitionDays = deal.stageHistory.reduce((sum, t) => {
      const dur = t.durationMs ?? 0;
      return sum + dur / 86400000;
    }, 0) / deal.stageHistory.length;

    if (avgTransitionDays > 0 && daysInStage > avgTransitionDays) {
      velocityScore = Math.round(clamp(daysInStage / avgTransitionDays, 1, 5) / 5 * 100);
    } else {
      velocityScore = 20;
    }
  }
  signals.push({
    label: 'Velocity decay',
    value: velocityScore,
    weight: 15,
    detail: deal.stageHistory?.length
      ? `${deal.stageHistory.length} transitions, now stalling`
      : 'No transitions yet',
  });

  // 5. Time since last touch (15pts) — cold deals need action
  const daysSinceUpdate = getDaysFromDate(deal.updatedAt);
  const coldScore = Math.round(clamp(daysSinceUpdate / 14, 0, 1) * 100);
  signals.push({
    label: 'Going cold',
    value: coldScore,
    weight: 15,
    detail: daysSinceUpdate === 0 ? 'Updated today' : `${daysSinceUpdate}d since last touch`,
  });

  // Weighted total
  const total = Math.round(
    signals.reduce((sum, s) => sum + (s.value * s.weight) / 100, 0),
  );

  const score = clamp(total, 0, 100);

  const band: ThreatAssessment['band'] =
    score >= 75 ? 'critical'
    : score >= 50 ? 'high'
    : score >= 30 ? 'moderate'
    : 'low';

  return { score, band, signals };
}

/**
 * Find the top N deals by threat score from all active deals (for auto-populate).
 */
export function getTopThreats(
  deals: EnhancedDeal[],
  stages: PipelineStage[],
  count: number = 5,
): { deal: EnhancedDeal; assessment: ThreatAssessment }[] {
  const activeDeals = deals.filter(d => d.outcome === 'active');
  const totalStages = stages.length || 1;

  const scored = activeDeals.map(deal => {
    const stage = stages.find(s => s.id === deal.stageId);
    return {
      deal,
      assessment: computeThreatScore(deal, stage, totalStages, deals),
    };
  });

  scored.sort((a, b) => b.assessment.score - a.assessment.score);
  return scored.slice(0, count);
}
